import type { ChartBlockSchema } from '@/components/organisms/ai/AIChart/AIChart';
import type { FormBlockSchema } from '@/components/organisms/ai/AIForm/AIForm';
import type { SelectBlockSchema } from '@/components/organisms/ai/AISelect/AISelect';
import type { TableBlockSchema } from '@/components/organisms/ai/AITable/AITable';

export type InteractiveSchemaLanguage = 'chart' | 'select' | 'table' | 'form';

export type SchemaValidationResult =
  | { valid: true; schema: unknown }
  | { valid: false; message: string };

const CHART_TYPES = new Set(['bar', 'line', 'pie', 'area']);
const FORM_FIELD_TYPES = new Set([
  'text',
  'email',
  'number',
  'textarea',
  'select',
  'checkbox',
  'radio',
  'date',
]);

interface SchemaOptionObject {
  value: string;
  label: string;
  disabled?: boolean;
}

type SchemaOption = string | SchemaOptionObject;
type TableColumnCandidate = TableBlockSchema['columns'][number];
type FormFieldCandidate = FormBlockSchema['fields'][number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || isString(value);
}

function isOptionalBoolean(value: unknown): boolean {
  return value === undefined || typeof value === 'boolean';
}

function isOptionalFiniteNumber(value: unknown): boolean {
  return (
    value === undefined || (typeof value === 'number' && Number.isFinite(value))
  );
}

function isOptionalNumber(value: unknown): boolean {
  return value === undefined || typeof value === 'number';
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(isString);
}

function isOptionObject(value: unknown): value is SchemaOptionObject {
  return (
    isRecord(value) &&
    isString(value.value) &&
    isString(value.label) &&
    isOptionalBoolean(value.disabled)
  );
}

function isOptionsArray(value: unknown): value is SchemaOption[] {
  return (
    Array.isArray(value) &&
    value.every((item) => isString(item) || isOptionObject(item))
  );
}

function hasUniqueValues(values: string[]): boolean {
  return new Set(values).size === values.length;
}

function getOptionValue(option: SchemaOption): string {
  return typeof option === 'string' ? option : String(option.value);
}

function hasUniqueOptionValues(value: unknown): boolean {
  if (!isOptionsArray(value)) {
    return false;
  }

  return hasUniqueValues(value.map(getOptionValue));
}

function hasUniqueTableColumnKeys(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.every(isTableColumn) &&
    hasUniqueValues(value.map((column) => column.key))
  );
}

function hasUniqueFormFieldNames(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.every(isFormField) &&
    hasUniqueValues(value.map((field) => field.name))
  );
}

function formFieldsHaveUniqueOptions(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.every(
      (field) =>
        isFormField(field) &&
        (field.options === undefined || hasUniqueOptionValues(field.options)),
    )
  );
}

function isDataRows(value: unknown): value is Record<string, unknown>[] {
  return Array.isArray(value) && value.every(isRecord);
}

function isTableColumn(value: unknown): value is TableColumnCandidate {
  return (
    isRecord(value) &&
    isString(value.key) &&
    isString(value.label) &&
    isOptionalBoolean(value.sortable) &&
    isOptionalBoolean(value.editable) &&
    (value.width === undefined ||
      isString(value.width) ||
      typeof value.width === 'number')
  );
}

function isTablePagination(value: unknown): boolean {
  return (
    value === undefined ||
    typeof value === 'boolean' ||
    (isRecord(value) &&
      typeof value.pageSize === 'number' &&
      Number.isInteger(value.pageSize) &&
      value.pageSize > 0)
  );
}

function isFormField(value: unknown): value is FormFieldCandidate {
  if (!isRecord(value)) {
    return false;
  }

  return (
    isString(value.name) &&
    isString(value.label) &&
    isString(value.type) &&
    FORM_FIELD_TYPES.has(value.type) &&
    isOptionalString(value.placeholder) &&
    isOptionalBoolean(value.required) &&
    (value.options === undefined || isOptionsArray(value.options)) &&
    isOptionalFiniteNumber(value.rows) &&
    isOptionalFiniteNumber(value.min) &&
    isOptionalFiniteNumber(value.max)
  );
}

export function validateTableBlockSchema(
  schema: unknown,
): schema is TableBlockSchema {
  return (
    isRecord(schema) &&
    isString(schema.id) &&
    isOptionalString(schema.title) &&
    Array.isArray(schema.columns) &&
    schema.columns.every(isTableColumn) &&
    hasUniqueTableColumnKeys(schema.columns) &&
    isDataRows(schema.data) &&
    isOptionalBoolean(schema.selectable) &&
    isTablePagination(schema.pagination)
  );
}

export function validateFormBlockSchema(
  schema: unknown,
): schema is FormBlockSchema {
  return (
    isRecord(schema) &&
    isString(schema.id) &&
    isOptionalString(schema.title) &&
    isOptionalString(schema.description) &&
    Array.isArray(schema.fields) &&
    schema.fields.every(isFormField) &&
    hasUniqueFormFieldNames(schema.fields) &&
    formFieldsHaveUniqueOptions(schema.fields) &&
    isOptionalString(schema.submitLabel) &&
    (schema.layout === undefined ||
      schema.layout === 'vertical' ||
      schema.layout === 'horizontal')
  );
}

export function validateSelectBlockSchema(
  schema: unknown,
): schema is SelectBlockSchema {
  return (
    isRecord(schema) &&
    isString(schema.id) &&
    isOptionalString(schema.label) &&
    (schema.multiple === undefined || schema.multiple === false) &&
    isOptionsArray(schema.options) &&
    hasUniqueOptionValues(schema.options) &&
    (schema.defaultValue === undefined || isString(schema.defaultValue)) &&
    isOptionalBoolean(schema.required) &&
    isOptionalString(schema.placeholder)
  );
}

export function validateChartBlockSchema(
  schema: unknown,
): schema is ChartBlockSchema {
  return (
    isRecord(schema) &&
    isString(schema.id) &&
    isString(schema.type) &&
    CHART_TYPES.has(schema.type) &&
    isOptionalString(schema.title) &&
    isDataRows(schema.data) &&
    isOptionalString(schema.xKey) &&
    (schema.yKey === undefined ||
      isString(schema.yKey) ||
      isStringArray(schema.yKey)) &&
    (schema.colors === undefined || isStringArray(schema.colors)) &&
    isOptionalBoolean(schema.interactive) &&
    isOptionalNumber(schema.height)
  );
}

export function validateInteractiveSchema(
  language: InteractiveSchemaLanguage,
  schema: unknown,
): SchemaValidationResult {
  const valid = {
    chart: validateChartBlockSchema,
    select: validateSelectBlockSchema,
    table: validateTableBlockSchema,
    form: validateFormBlockSchema,
  }[language](schema);

  if (valid) {
    return { valid: true, schema };
  }

  return {
    valid: false,
    message: `Invalid ${language} schema: The configuration does not match the expected shape.`,
  };
}
